import { Settings, CheckCircle, Calendar, MapPin, ArrowRight, ClipboardCheck } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";
import { SEOHead } from "../SEOHead";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { FloatingContactButtons } from "../FloatingContactButtons";
import { companyInfo } from "../../data/mock";

export const VehicleServicingPage = () => {
  return (
    <div className="min-h-screen bg-slate-50">
      <SEOHead 
        title="Car Servicing Hackney | Vehicle Maintenance East London | MOT & Service"
        description="Professional car servicing & vehicle maintenance in Hackney & East London. Full service, MOT, diagnostics, repairs. Qualified mechanics. Call 07492 639223 to book service."
        keywords="car servicing hackney, vehicle maintenance, car service east london, mot service, full service, interim service, car repairs hackney, vehicle servicing london"
      />
      <Header />

      <section className="bg-gradient-to-br from-slate-900 via-gray-800 to-zinc-900 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-block bg-gray-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              ⚙️ Professional Workshop
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Vehicle Servicing & Maintenance in Hackney
            </h1>
            <p className="text-xl text-gray-200 mb-8">
              Professional vehicle servicing, MOT preparation, and comprehensive maintenance across Hackney & East London. Qualified mechanics, quality parts, competitive pricing. Keep your vehicle running smoothly and safely.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={`tel:${companyInfo.phone}`}>
                <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-8 py-6">
                  <Settings className="w-5 h-5 mr-2" />
                  Call: {companyInfo.phone}
                </Button>
              </a>
              <Link to="/#quote">
                <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8 py-6">
                  Book Service
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="py-8 bg-white border-b">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-600">Qualified</p>
              <p className="text-sm text-slate-600">Mechanics</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-600">Quality</p>
              <p className="text-sm text-slate-600">Parts Used</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-600">Warranty</p>
              <p className="text-sm text-slate-600">On All Work</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-slate-600">Competitive</p>
              <p className="text-sm text-slate-600">Pricing</p>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-slate-900 mb-6">
              Comprehensive Vehicle Servicing in Hackney
            </h2>
            <p className="text-lg text-slate-700 leading-relaxed mb-6">
              Ovawell Autos provides professional vehicle servicing and maintenance for all makes and models across Hackney and East London. From routine services to complex repairs, our qualified mechanics ensure your vehicle is safe, reliable, and running at peak performance. We use quality parts and provide warranty on all work.
            </p>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Services We Offer
            </h3>
            <div className="grid md:grid-cols-2 gap-4 mb-8">
              {[
                "Full vehicle service",
                "Interim service",
                "MOT preparation & repairs",
                "Engine diagnostics",
                "Brake service & repairs",
                "Clutch replacement",
                "Exhaust system repairs",
                "Suspension repairs",
                "Timing belt replacement",
                "Air conditioning service",
                "Electrical diagnostics",
                "Cooling system service",
                "General repairs",
                "Tyre fitting & balancing",
                "Battery replacement",
                "Pre-purchase inspections"
              ].map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <ClipboardCheck className="w-6 h-6 text-slate-600 flex-shrink-0 mt-1" />
                  <span className="text-slate-700">{item}</span>
                </div>
              ))}
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Why Choose Our Workshop?
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Settings className="w-8 h-8 text-slate-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Qualified Mechanics</h4>
                      <p className="text-slate-600">
                        All our mechanics are qualified and experienced. We invest in ongoing training to stay current with modern vehicle technology and repair techniques.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <CheckCircle className="w-8 h-8 text-slate-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Quality Parts & Warranty</h4>
                      <p className="text-slate-600">
                        We use quality replacement parts and provide warranty on all work. Your satisfaction and vehicle safety are our priority.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Calendar className="w-8 h-8 text-slate-600 flex-shrink-0" />
                    <div>
                      <h4 className="font-bold text-lg text-slate-900 mb-2">Flexible Booking</h4>
                      <p className="text-slate-600">
                        Book at a time that suits you. We offer flexible appointment times and can often accommodate same-day servicing for urgent work.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <h3 className="text-2xl font-bold text-slate-900 mb-4 mt-8">
              Service Types Explained
            </h3>
            <div className="space-y-4 mb-8">
              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">Full Service</h4>
                  <p className="text-slate-600 mb-2">
                    Comprehensive inspection and maintenance. Recommended annually or every 12,000 miles. Includes:
                  </p>
                  <ul className="text-sm text-slate-600 space-y-1">
                    <li>• Oil & filter change</li>
                    <li>• Air filter replacement</li>
                    <li>• Brake inspection</li>
                    <li>• Fluid checks & top-ups</li>
                    <li>• Lights & electrical checks</li>
                    <li>• Tyre condition & pressure</li>
                  </ul>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <h4 className="font-bold text-lg text-slate-900 mb-2">Interim Service</h4>
                  <p className="text-slate-600">
                    Basic maintenance for high-mileage drivers. Recommended every 6 months or 6,000 miles. Includes essential checks and oil change.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-gradient-to-br from-slate-900 to-gray-900 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Book Your Vehicle Service Today
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Professional servicing & maintenance in Hackney. Keep your vehicle safe and reliable.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" className="bg-white text-slate-900 hover:bg-slate-100 text-lg px-8 py-6">
                <Settings className="w-5 h-5 mr-2" />
                Call {companyInfo.phone}
              </Button>
            </a>
            <Link to="/#quote">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-slate-900 text-lg px-8 py-6">
                Get Quote
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingContactButtons />
    </div>
  );
};
